"use strict";
/**
 * Thread membership progress digests for multi-tab / multi-device fan-out.
 *
 * Piggybacks on history focus SSE (same transport as recentDigest).
 * Patches are compact: threadId + memberId + status/role — not full pill UI.
 *
 * LWW per membership using patch `at` (epoch ms). localStorage holds last-applied
 * times so a stale SSE replay does not clobber a newer local/server write.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isThreadCacheStorageKey = exports.patchFromMemberChange = exports.applyThreadMemberDigestToCache = exports.extractThreadMemberDigestFromFocusData = exports.normalizeThreadMemberPatch = exports.buildThreadMemberDigest = exports.getThreadMemberPatchAt = exports.touchThreadMemberPatchAt = exports.threadMemberAtStorageKey = exports.THREAD_MEMBER_AT_PREFIX = exports.THREAD_MEMBER_DIGEST_DEFAULT_LIMIT = void 0;
var threadStore_1 = require("./threadStore");
exports.THREAD_MEMBER_DIGEST_DEFAULT_LIMIT = 40;
exports.THREAD_MEMBER_AT_PREFIX = 'audroam.threadMemberStatusAt';
function threadMemberAtStorageKey(personId) {
    return "".concat(exports.THREAD_MEMBER_AT_PREFIX, ":").concat(personId !== null && personId !== void 0 ? personId : 'anon');
}
exports.threadMemberAtStorageKey = threadMemberAtStorageKey;
function memberAtKey(threadId, memberId, memberType) {
    if (memberType === void 0) { memberType = 'pilot_note'; }
    return "".concat(threadId, "\0").concat(memberType || 'pilot_note', "\0").concat((0, threadStore_1.normalizeMemberId)(memberId));
}
function readAtMap(personId, storage) {
    try {
        var raw = storage.getItem(threadMemberAtStorageKey(personId));
        if (!raw)
            return {};
        var parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch (_a) {
        return {};
    }
}
function writeAtMap(personId, map, storage) {
    try {
        // Cap map size — drop oldest half if huge
        var keys = Object.keys(map);
        if (keys.length > 500) {
            var sorted = keys
                .map(function (k) { return ({ k: k, at: map[k] || 0 }); })
                .sort(function (a, b) { return a.at - b.at; });
            var drop = sorted.slice(0, keys.length - 400);
            for (var _i = 0, drop_1 = drop; _i < drop_1.length; _i++) {
                var d = drop_1[_i];
                delete map[d.k];
            }
        }
        storage.setItem(threadMemberAtStorageKey(personId), JSON.stringify(map));
    }
    catch (_a) {
        /* quota */
    }
}
/** Record that we applied (or authored) a membership change at `at`. */
function touchThreadMemberPatchAt(personId, threadId, memberId, at, storage, memberType) {
    if (storage === void 0) { storage = localStorage; }
    if (memberType === void 0) { memberType = 'pilot_note'; }
    if (threadId == null || memberId == null || !at)
        return;
    var map = readAtMap(personId, storage);
    var key = memberAtKey(threadId, memberId, memberType);
    var prev = map[key] || 0;
    if (at >= prev) {
        map[key] = at;
        writeAtMap(personId, map, storage);
    }
}
exports.touchThreadMemberPatchAt = touchThreadMemberPatchAt;
function getThreadMemberPatchAt(personId, threadId, memberId, storage, memberType) {
    if (storage === void 0) { storage = localStorage; }
    if (memberType === void 0) { memberType = 'pilot_note'; }
    var map = readAtMap(personId, storage);
    return map[memberAtKey(threadId, memberId, memberType)] || 0;
}
exports.getThreadMemberPatchAt = getThreadMemberPatchAt;
function buildThreadMemberDigest(patches, opts) {
    var _a;
    if (!Array.isArray(patches) || patches.length === 0)
        return null;
    var limit = Math.max(1, Math.min(100, (_a = opts === null || opts === void 0 ? void 0 : opts.limit) !== null && _a !== void 0 ? _a : exports.THREAD_MEMBER_DIGEST_DEFAULT_LIMIT));
    var cleaned = [];
    for (var i = 0; i < patches.length && cleaned.length < limit; i++) {
        var p = normalizeThreadMemberPatch(patches[i]);
        if (p)
            cleaned.push(p);
    }
    if (!cleaned.length)
        return null;
    return {
        at: typeof (opts === null || opts === void 0 ? void 0 : opts.at) === 'number' && opts.at > 0 ? opts.at : Date.now(),
        patches: cleaned,
        deviceId: opts === null || opts === void 0 ? void 0 : opts.deviceId,
    };
}
exports.buildThreadMemberDigest = buildThreadMemberDigest;
function normalizeThreadMemberPatch(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    var threadId = raw.threadId != null ? String(raw.threadId) : raw.thread_id != null ? String(raw.thread_id) : '';
    var memberIdRaw = raw.memberId != null ? raw.memberId : raw.member_id;
    if (!threadId || memberIdRaw == null || memberIdRaw === '')
        return null;
    var at = typeof raw.at === 'number' && raw.at > 0 ? raw.at : Date.now();
    var patch = {
        threadId: threadId,
        memberId: (0, threadStore_1.normalizeMemberId)(memberIdRaw),
        at: at,
    };
    if (raw.memberType != null || raw.member_type != null) {
        patch.memberType = String(raw.memberType || raw.member_type || 'pilot_note');
    }
    if (raw.status !== undefined)
        patch.status = raw.status == null ? null : String(raw.status);
    if (raw.role !== undefined)
        patch.role = raw.role == null ? null : String(raw.role);
    if (raw.pathHint !== undefined || raw.path_hint !== undefined) {
        var h = raw.pathHint !== undefined ? raw.pathHint : raw.path_hint;
        patch.pathHint = h == null || h === '' ? null : String(h);
    }
    if (raw.removed === true || raw.removed === 1 || raw.removed === '1') {
        patch.removed = true;
    }
    return patch;
}
exports.normalizeThreadMemberPatch = normalizeThreadMemberPatch;
function extractThreadMemberDigestFromFocusData(data) {
    if (!data || typeof data !== 'object')
        return null;
    var dig = data.threadMemberDigest;
    if (!dig || typeof dig !== 'object')
        return null;
    if (!Array.isArray(dig.patches) || dig.patches.length === 0)
        return null;
    var patches = dig.patches
        .map(normalizeThreadMemberPatch)
        .filter(function (p) { return !!p; });
    if (!patches.length)
        return null;
    return {
        at: typeof dig.at === 'number' && dig.at > 0 ? dig.at : Date.now(),
        patches: patches,
        deviceId: dig.deviceId != null ? String(dig.deviceId) : undefined,
    };
}
exports.extractThreadMemberDigestFromFocusData = extractThreadMemberDigestFromFocusData;
/**
 * Apply patches to person-scoped local cache (write-through).
 * Skips patches older than last-applied `at` for that membership.
 */
function applyThreadMemberDigestToCache(personId, digest, storage) {
    var _a;
    if (storage === void 0) { storage = localStorage; }
    var empty = {
        applied: 0,
        skipped: 0,
        needsThreadPull: false,
        changedMemberIds: [],
    };
    if (!digest || !Array.isArray(digest.patches) || !digest.patches.length) {
        return empty;
    }
    if (personId == null)
        return empty;
    var atMap = readAtMap(personId, storage);
    var threads = (0, threadStore_1.listThreads)(personId, storage);
    var threadIds = new Set(threads.map(function (t) { return t.id; }));
    var applied = 0;
    var skipped = 0;
    var needsThreadPull = false;
    var changed = new Set();
    // Newest first so older duplicates in the same batch lose
    var ordered = digest.patches
        .map(normalizeThreadMemberPatch)
        .filter(function (p) { return !!p; })
        .slice()
        .sort(function (a, b) { return b.at - a.at; });
    var seen = new Set();
    var _loop_1 = function (patch) {
        var mtype = patch.memberType || 'pilot_note';
        var key = memberAtKey(patch.threadId, patch.memberId, mtype);
        if (seen.has(key)) {
            skipped += 1;
            return "continue";
        }
        seen.add(key);
        var prevAt = atMap[key] || 0;
        if (patch.at < prevAt) {
            skipped += 1;
            return "continue";
        }
        // Equal at: still apply once (idempotent field write)
        if (patch.at === prevAt && prevAt > 0) {
            // Allow re-apply for same ms (SSE echo) — cheap upsert
        }
        if (!threadIds.has(patch.threadId)) {
            needsThreadPull = true;
        }
        if (patch.removed) {
            (0, threadStore_1.removeMember)(personId, patch.threadId, patch.memberId, storage);
            atMap[key] = patch.at;
            applied += 1;
            changed.add(String(patch.memberId));
            return "continue";
        }
        var priorList = (0, threadStore_1.listMembers)(personId, patch.threadId, storage);
        var prior = priorList.find(function (m) { return m.memberId === patch.memberId && (m.memberType || 'pilot_note') === mtype; });
        var fields = {
            threadId: patch.threadId,
            memberId: patch.memberId,
            memberType: mtype,
            role: (prior === null || prior === void 0 ? void 0 : prior.role) || 'line_item',
            status: (prior === null || prior === void 0 ? void 0 : prior.status) || 'todo',
            pathHint: prior === null || prior === void 0 ? void 0 : prior.pathHint,
            sort: (_a = prior === null || prior === void 0 ? void 0 : prior.sort) !== null && _a !== void 0 ? _a : 0,
            linkedAt: (prior === null || prior === void 0 ? void 0 : prior.linkedAt) || new Date().toISOString(),
            linkedBy: prior === null || prior === void 0 ? void 0 : prior.linkedBy,
        };
        if (patch.status !== undefined) {
            fields.status =
                patch.status == null || patch.status === '' ? undefined : String(patch.status);
        }
        if (patch.role !== undefined && patch.role != null && patch.role !== '') {
            fields.role = String(patch.role);
        }
        if (patch.pathHint !== undefined) {
            fields.pathHint =
                patch.pathHint == null || patch.pathHint === ''
                    ? undefined
                    : String(patch.pathHint);
        }
        var upserted = (0, threadStore_1.upsertLocalMember)(personId, fields, storage);
        if (upserted) {
            atMap[key] = patch.at;
            applied += 1;
            changed.add(String(patch.memberId));
        }
        else {
            skipped += 1;
        }
    };
    for (var _i = 0, ordered_1 = ordered; _i < ordered_1.length; _i++) {
        var patch = ordered_1[_i];
        _loop_1(patch);
    }
    writeAtMap(personId, atMap, storage);
    return {
        applied: applied,
        skipped: skipped,
        needsThreadPull: needsThreadPull,
        changedMemberIds: Array.from(changed),
    };
}
exports.applyThreadMemberDigestToCache = applyThreadMemberDigestToCache;
/**
 * Build a single-member patch for local publish after a successful mutation.
 */
function patchFromMemberChange(threadId, memberId, change, at) {
    return {
        threadId: String(threadId),
        memberId: (0, threadStore_1.normalizeMemberId)(memberId),
        memberType: change.memberType || 'pilot_note',
        status: change.status,
        role: change.role,
        pathHint: change.pathHint,
        removed: change.removed === true,
        at: typeof at === 'number' && at > 0 ? at : Date.now(),
    };
}
exports.patchFromMemberChange = patchFromMemberChange;
/** True if key is a thread list/members localStorage key (for storage events). */
function isThreadCacheStorageKey(key) {
    if (!key)
        return false;
    return (key.startsWith(threadStore_1.THREADS_STORAGE_PREFIX + ':') ||
        key.startsWith(threadStore_1.THREAD_MEMBERS_STORAGE_PREFIX + ':') ||
        key.startsWith(exports.THREAD_MEMBER_AT_PREFIX + ':'));
}
exports.isThreadCacheStorageKey = isThreadCacheStorageKey;
